import { HttpErrorResponse } from '@angular/common/http'
import { computed, Injectable, signal } from '@angular/core'
import { StocksRepository } from '../stocks.repository'
import { STOCK_DEFAULT_FIELDS } from '../consts/stock.const'
import {
  StocksListingData,
  StocksListingPage,
} from '../models/stock-listing.model'
import { StockSearchParams, StockField } from '../models/stock-search.model'
import { Stock } from '../models/stock.entity'
import { mergeById } from '../../../libs/filter.lib'
import { Observable } from 'rxjs'
import { WarehousesService } from '../../warehouses'

interface StocksListingState {
  data?: Stock[]
  extData?: StocksListingData
  dropdownOptions?: StocksListingData
  totalCount?: number
  fields: StockField[]
  filters?: StockSearchParams
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
}

const STOCKS_INITIAL_STATE: StocksListingState = {
  fields: STOCK_DEFAULT_FIELDS,
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class StocksListingStore {
  constructor(
    private stocksRepository: StocksRepository,
    private warehousesService: WarehousesService,
  ) {}

  private state = signal<StocksListingState>(STOCKS_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly extData = computed(() => this.state().extData)
  readonly dropdownOptions = computed(() => this.state().dropdownOptions)
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly fields = computed(() => this.state().fields)
  readonly stocks = computed(() => this.state().data || [])

  // Effects

  async search(params?: StockSearchParams, returnAll?: boolean): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.stocksRepository
        .searchStocks$(
          this.parseStateFilterParams(this.state(), params),
          this.state().fields ?? [],
          this.state().extData,
          returnAll,
        )
        .subscribe({
          next: (data) => {
            this.setData(data)
            resolve()
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  async searchLookup(event: {
    dataSourceKey: string
    q?: string
    dataKeyField?: string
    dataDisplayField?: string
    offset?: number
    limit?: number
  }): Promise<void> {
    type LookupKey = keyof StocksListingData
    type ArrayElement<T> = T extends (infer U)[] ? U : never
    type LookupValue = ArrayElement<NonNullable<StocksListingData[LookupKey]>>

    const queryKey = `${event.dataDisplayField ?? 'name'}:ct`
    const offset = event.offset ?? 0
    const limit = event.limit ?? 50
    const query = {
      offset,
      limit,
      ...(event.q ? { [queryKey]: event.q } : {}),
    } as Record<string, unknown>

    const serviceMap: Partial<
      Record<LookupKey, () => Observable<LookupValue[]>>
    > = {
      warehouses: () => this.warehousesService.list$(query),
    }

    const search$ = serviceMap[event.dataSourceKey as LookupKey]
    if (!search$) return

    search$().subscribe((items) => {
      this.state.update((s) => {
        const key = event.dataSourceKey as LookupKey
        const prev = (s.dropdownOptions?.[key] ?? []) as { _id: string }[]
        const incoming = items as { _id: string }[]
        const merged = prev.length > 0 ? mergeById(prev, incoming) : incoming
        return {
          ...s,
          dropdownOptions: {
            ...s.extData,
            [event.dataSourceKey]: merged as LookupValue[],
          },
        }
      })
    })
  }

  // Reducers

  setData(data: StocksListingPage) {
    const extData = this.extData()
    const newExtData = {
      warehouses: mergeById(extData?.warehouses, data.extData?.warehouses),
    }
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      extData: newExtData,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: StockSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: StockSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: StockSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof StockSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  setFields(fields: StockField[]) {
    this.state.set({ ...this.state(), fields })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }

  resetLookups() {
    this.state.set({
      ...this.state(),
      dropdownOptions: undefined,
      extData: undefined,
    })
  }

  private parseStateFilterParams(
    state: StocksListingState,
    filters?: StockSearchParams,
  ): StockSearchParams {
    let params: StockSearchParams = filters || {}
    if (filters?.sort) {
      params.sort = filters.sort
    }

    params = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => key !== undefined && value !== undefined,
      ),
    )

    return params
  }
}
